import {StyleProps} from '..'

import colors from './colors'

interface FocusOutline {
  boxShadow: string | string[]
  borderColor: string | string[]
  borderWidth?: string | number | Array<string | number>
  borderStyle?: string
}

interface FocusOutlineWithOffset extends FocusOutline {
  borderRadius?: number
  offset: {
    x: number
    y: number
  }
}

function withOffset(
  options: FocusOutline | FocusOutlineWithOffset,
): options is FocusOutlineWithOffset {
  return typeof (options as FocusOutlineWithOffset).offset !== 'undefined'
}

export function focusOutline(options: FocusOutline | FocusOutlineWithOffset): StyleProps {
  const {borderColor, boxShadow, borderWidth = '1px', borderStyle = 'solid'} = options

  const outline = {
    borderColor,
    borderWidth,
    borderStyle,
    boxShadow,
  }

  if (withOffset(options)) {
    const {borderRadius = 0, offset} = options

    return {
      '[tabindex="-1"]': {
        position: 'relative',
        ':before': {
          content: "''",
          position: 'absolute',
          borderRadius,
          top: -offset.y,
          right: -offset.x,
          bottom: -offset.y,
          left: -offset.x,
          ...outline,
        },
      },
    }
  }

  return outline
}

export const defaultOptions = {
  borderColor: colors.primary80,
  boxShadow: `0 0 0 4 ${colors.primary40}`,
  borderWidth: '1px',
  borderRadius: 4,
}

export const defaultOffset = {
  x: 5,
  y: 3,
}

export type Outlines = {
  [key: string]: StyleProps
}

/**
 * Default focus outline styles
 */
export const outlines: Outlines = {
  default: focusOutline({...defaultOptions}),
  withOffset: focusOutline({...defaultOptions, offset: {...defaultOffset}}),
  defaultOutline: {
    outlineWidth: 4,
    outlineStyle: 'solid',
    outlineColor: colors.primary40,
    outlineOffset: 3,
  },
}

export default {
  focusOutline,
  defaultOptions,
  defaultOffset,
  outlines,
}
